import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { isNullOrUndefined } from "../lib/Util";

export const parseGQLResponseError = (error: unknown): string => {
  if (!error) {
    return "Unknown server error.";
  }

  if (CombinedGraphQLErrors.is(error) && error.errors.length > 0) {
    return error.errors[0].message;
  }

  if ((error as Error).message) {
    return (error as Error).message;
  }

  return "Unknown server error.";
};

type GraphQLPageInfo = {
  startCursor?: string | null;
  endCursor?: string | null;
  hasNextPage?: boolean | null;
  hasPreviousPage?: boolean | null;
};

type GraphQLConnection<TNode> = {
  pageInfo?: GraphQLPageInfo | null;
  edges?: { node: TNode }[] | null;
};

export const parsePagedEntries = <TNode = unknown, TResult = TNode>(
  entries: Record<string, unknown> | null | undefined,
  key: string,
  constructor?: (node: TNode) => TResult
) => {
  // Repeated code we use to parse data from Apollo GQL Queries
  const raw = (entries ? entries[key] : null) as GraphQLConnection<TNode> | null;
  const pageInfo = (raw?.pageInfo ?? {}) as GraphQLPageInfo;
  const startCursor = pageInfo.startCursor;
  const endCursor = pageInfo.endCursor;
  let results: TResult[] = [];
  if (raw && raw.edges) {
    if (constructor === undefined) {
      results = raw.edges.map((edge) => edge.node) as unknown as TResult[];
    } else {
      results = raw.edges.map((edge) => constructor(edge.node));
    }
  }
  const showPagination = pageInfo && (pageInfo.hasNextPage || pageInfo.hasPreviousPage);
  return { results, startCursor, endCursor, pageInfo, showPagination };
};

export const checkForResults = (results: unknown) => {
  let hasResults = false;
  if (Array.isArray(results)) {
    hasResults = results.length > 0;
  } else if (typeof results === "object" && !isNullOrUndefined(results)) {
    hasResults = Object.keys(results).length > 0;
  } else {
    hasResults = !isNullOrUndefined(results);
  }
  return hasResults;
};

type ParsedRows<TResult> = {
  results: TResult[] | null;
  pageInfo: unknown;
  hasResults: boolean;
  isPageable: boolean;
};

export const parseRows = <TResult>(
  data: unknown,
  nodeName: string,
  hasResultsFn?: ((data: unknown) => boolean) | null
) => {
  // Takes a GQL query result and extracts the data required either returning the node if no paging, or extracting the
  // `edge.node` from each row result simplifying this common mapping transformation.

  const meta: ParsedRows<TResult> = {
    results: null,
    pageInfo: null,
    hasResults: false,
    isPageable: false,
  };
  if (data) {
    const node = (data as Record<string, unknown>)[nodeName];
    const results = node as GraphQLConnection<TResult> | null;
    if (results && (results.pageInfo || results.edges)) {
      meta.results = (results.edges ?? []).map((edge) => edge.node);
      meta.pageInfo = results.pageInfo;
      meta.isPageable = results.pageInfo
        ? Boolean(results.pageInfo.hasNextPage || results.pageInfo.hasPreviousPage)
        : false;
    } else {
      meta.results = node as TResult[] | null;
    }
    meta.hasResults = checkForResults(meta.results);
    if (!meta.hasResults && hasResultsFn instanceof Function) {
      meta.hasResults = hasResultsFn(data);
    }
  }
  return meta;
};
